import { useCallback, useEffect, useState } from "react";
import {
  fetchWebhooks,
  fetchWebhookIntegrations,
  createWebhook,
  patchWebhook,
  deleteWebhook,
  fetchNotificationSettings,
  createNotificationSetting,
  patchNotificationSetting,
  deleteNotificationSetting,
  fetchLexAiEvents,
  fetchOrgEndpointCatalog,
  enableLexAi,
  fetchLexAiLogs,
  testLexAiWebhook,
  getErrorDetail
} from "../../../services/notificationWorkflowService";
import {
  emptyOAuthConfig,
  buildOAuthConfigFromForm,
  validateOAuthConfig,
  isAdminUser,
  getUserRole,
  getJwtPayload,
  parseValidationErrors,
  formatApiError,
  FALLBACK_AUTH_TYPES
} from "./webhookHelpers";
import { STORAGE_KEYS } from "../../../constants/storage";
import { getUserDetails } from "../../../services/authService";
import { isOAuthAuthType, getOAuthRequiredFields } from "./WebhookOAuthFields";

export function useNotificationWorkflow() {
  const [tab, setTab] = useState("webhooks");
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, isError = false) => {
    setToast({ message, isError });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const [webhooks, setWebhooks] = useState([]);
  const [whIntegrations, setWhIntegrations] = useState({ auth_types: [], payload_transforms: [] });
  const [whName, setWhName] = useState("");
  const [whUrl, setWhUrl] = useState("");
  const [whAuthType, setWhAuthType] = useState("none");
  const [whOAuthConfig, setWhOAuthConfig] = useState(emptyOAuthConfig());
  const [whFieldErrors, setWhFieldErrors] = useState({});
  const [whResponse, setWhResponse] = useState(null);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [whPatchSaving, setWhPatchSaving] = useState(false);

  const [notificationRows, setNotificationRows] = useState([]);
  const [lexaiEvents, setLexaiEvents] = useState([]);
  const [nsEventId, setNsEventId] = useState("");
  const [nsWebhookId, setNsWebhookId] = useState("");
  const [nsResponse, setNsResponse] = useState(null);
  const [nsTestResponse, setNsTestResponse] = useState(null);

  // A subscription targets EXACTLY ONE destination: a webhook URL, or a
  // catalogued endpoint. The backend enforces this with a CHECK constraint, so
  // the form is a toggle rather than two independently-fillable sections.
  const [nsDestKind, setNsDestKind] = useState("webhook"); // "webhook" | "endpoint"
  const [nsEndpointId, setNsEndpointId] = useState("");
  const [endpointCatalog, setEndpointCatalog] = useState([]);
  // Optional overrides. Left blank, the backend forwards the event envelope's
  // `data` object as the request body and applies no deduplication.
  const [nsPayloadTemplate, setNsPayloadTemplate] = useState("");
  const [nsDedupKeyTemplate, setNsDedupKeyTemplate] = useState("");
  const [nsDedupWindow, setNsDedupWindow] = useState("3600");

  const [lexaiApiId, setLexaiApiId] = useState("");
  const [lexaiResponse, setLexaiResponse] = useState(null);

  const [logFilter, setLogFilter] = useState("");
  const [logLimit, setLogLimit] = useState("20");
  const [logs, setLogs] = useState(null);

  const loadWebhooks = useCallback(
    async (silent = false) => {
      try {
        const data = await fetchWebhooks();
        setWebhooks(data);
      } catch (e) {
        if (!silent) showToast(getErrorDetail(e), true);
      }
    },
    [showToast]
  );

  const loadNotificationSettingsList = useCallback(async () => {
    try {
      const data = await fetchNotificationSettings();
      setNotificationRows(data);
    } catch {
      /* ignore */
    }
  }, []);

  const loadLexAIEventsList = useCallback(
    async (silent = false) => {
      try {
        const events = await fetchLexAiEvents();
        setLexaiEvents(events);
        setNsEventId((cur) => {
          if (cur && events.some((e) => e.id === cur)) return cur;
          return events[0]?.id ?? "";
        });
      } catch (e) {
        if (!silent) showToast(getErrorDetail(e), true);
      }
    },
    [showToast]
  );

  const loadEndpointCatalog = useCallback(async () => {
    try {
      setEndpointCatalog(await fetchOrgEndpointCatalog());
    } catch {
      // Non-fatal: without a catalogue the endpoint destination option is simply
      // unavailable, and webhook subscriptions keep working.
      setEndpointCatalog([]);
    }
  }, []);

  const loadWebhookIntegrations = useCallback(async () => {
    try {
      const data = await fetchWebhookIntegrations();
      const authTypes =
        Array.isArray(data.auth_types) && data.auth_types.length > 0
          ? data.auth_types
          : FALLBACK_AUTH_TYPES;
      setWhIntegrations({ ...data, auth_types: authTypes });
      const defaultAuth = authTypes.find((a) => a.value === "none")?.value ?? "none";
      setWhAuthType((cur) => {
        if (cur && authTypes.some((a) => a.value === cur)) return cur;
        return defaultAuth;
      });
    } catch (e) {
      setWhIntegrations({ auth_types: FALLBACK_AUTH_TYPES, payload_transforms: [] });
      setWhAuthType("none");
      showToast(getErrorDetail(e), true);
    }
  }, [showToast]);

  // Persist JWT role into user details when missing (password login omits it).
  useEffect(() => {
    const role = getUserRole();
    if (!role) return;
    const details = getUserDetails();
    if (details?.role) return;
    const claims = getJwtPayload();
    if (!claims) return;
    localStorage.setItem(
      STORAGE_KEYS.USER_DETAILS,
      JSON.stringify({
        name: details?.name ?? claims.name ?? claims.email?.split("@")[0],
        email: details?.email ?? claims.email,
        organization_id: details?.organization_id ?? claims.organization_id ?? null,
        role
      })
    );
  }, []);

  useEffect(() => {
    loadWebhooks(true);
    loadWebhookIntegrations();
  }, [loadWebhooks, loadWebhookIntegrations]);

  useEffect(() => {
    if (tab === "notifications") {
      loadWebhooks(true);
      loadLexAIEventsList(true);
      loadNotificationSettingsList();
      loadEndpointCatalog();
    }
    if (tab === "lexai") loadLexAIEventsList();
  }, [
    tab,
    loadWebhooks,
    loadLexAIEventsList,
    loadNotificationSettingsList,
    loadEndpointCatalog
  ]);

  const activeWebhooks = webhooks.filter((w) => w.is_active);

  const onCreateWebhook = async () => {
    const name = whName.trim();
    const url = whUrl.trim();
    setWhFieldErrors({});
    if (!name || !url) {
      showToast("Name and URL are required", true);
      return;
    }

    const authType = whAuthType || "none";
    if (isOAuthAuthType(authType) && !isAdminUser()) {
      showToast("OAuth webhooks require admin role", true);
      return;
    }

    const payload = { name, webhook_url: url };
    if (authType && authType !== "none") {
      payload.auth_type = authType;
      if (isOAuthAuthType(authType)) {
        const required = getOAuthRequiredFields(whIntegrations, authType);
        const v = validateOAuthConfig(whOAuthConfig, required);
        if (Object.keys(v).length) {
          setWhFieldErrors(v);
          showToast("Please fill in all OAuth fields", true);
          return;
        }
        payload.auth_config = buildOAuthConfigFromForm(whOAuthConfig);
      }
    }

    try {
      const r = await createWebhook(payload);
      setWhResponse({ ok: true, text: JSON.stringify(r.data, null, 2) });
      showToast("Webhook added");
      setWhName("");
      setWhUrl("");
      setWhOAuthConfig(emptyOAuthConfig());
      setWhAuthType(whIntegrations.auth_types?.find((a) => a.value === "none")?.value ?? "none");
      loadWebhooks();
    } catch (e) {
      const { fieldErrors } = parseValidationErrors(e);
      if (Object.keys(fieldErrors).length) setWhFieldErrors(fieldErrors);
      setWhResponse({
        ok: false,
        text: JSON.stringify(e.response?.data ?? e.message, null, 2)
      });
      showToast(`Failed: ${formatApiError(e)}`, true);
    }
  };

  const onPatchWebhook = async (id, payload, setModalFieldErrors) => {
    setWhPatchSaving(true);
    try {
      await patchWebhook(id, payload);
      showToast("Webhook updated");
      setEditingWebhook(null);
      loadWebhooks();
    } catch (e) {
      const { fieldErrors } = parseValidationErrors(e);
      if (setModalFieldErrors && Object.keys(fieldErrors).length) {
        setModalFieldErrors(fieldErrors);
      }
      const status = e?.response?.status;
      if (status === 409) {
        showToast("Cannot change URL on an authenticated webhook", true);
      } else {
        showToast(formatApiError(e), true);
      }
      throw e;
    } finally {
      setWhPatchSaving(false);
    }
  };

  const onToggleWebhook = async (id, currentlyActive) => {
    try {
      await patchWebhook(id, { is_active: !currentlyActive });
      showToast(currentlyActive ? "Webhook disabled" : "Webhook enabled");
      loadWebhooks();
    } catch (e) {
      showToast(getErrorDetail(e), true);
    }
  };

  const onDeleteWebhook = async (id, name) => {
    if (
      !window.confirm(
        `Delete webhook "${name}"? This will also remove all notification settings linked to it.`
      )
    ) {
      return;
    }
    try {
      await deleteWebhook(id);
      showToast("Webhook deleted");
      loadWebhooks();
      loadNotificationSettingsList();
    } catch (e) {
      showToast(getErrorDetail(e), true);
    }
  };

  const onCreateNS = async () => {
    if (!nsEventId) {
      showToast("Please select an event", true);
      return;
    }

    const isEndpoint = nsDestKind === "endpoint";

    if (!isEndpoint && !nsWebhookId) {
      showToast("Please select a webhook", true);
      return;
    }
    if (isEndpoint && !nsEndpointId) {
      showToast("Please select an endpoint", true);
      return;
    }

    // Parse the payload template here rather than letting the backend reject it:
    // a JSON typo should be a field-level message, not a 422 blob.
    let payloadTemplate = null;
    if (isEndpoint && nsPayloadTemplate.trim()) {
      try {
        payloadTemplate = JSON.parse(nsPayloadTemplate);
      } catch (err) {
        setNsResponse({ ok: false, text: `Payload template is not valid JSON: ${err.message}` });
        showToast("Payload template is not valid JSON", true);
        return;
      }
      if (payloadTemplate === null || typeof payloadTemplate !== "object" || Array.isArray(payloadTemplate)) {
        showToast("Payload template must be a JSON object", true);
        return;
      }
    }

    const dedupWindow = Number(nsDedupWindow);
    if (isEndpoint && nsDedupKeyTemplate.trim() && (!Number.isFinite(dedupWindow) || dedupWindow <= 0)) {
      showToast("Duplicate window must be a positive number of seconds", true);
      return;
    }

    const body = isEndpoint
      ? {
          lexai_event_id: nsEventId,
          provider_api_endpoint_id: nsEndpointId,
          // Omit rather than send null: the backend distinguishes "not set"
          // (use the default mapping) from an explicit value.
          ...(payloadTemplate ? { payload_template: payloadTemplate } : {}),
          ...(nsDedupKeyTemplate.trim()
            ? { dedup_key_template: nsDedupKeyTemplate.trim(), dedup_window_seconds: dedupWindow }
            : {}),
          is_enabled: true
        }
      : {
          lexai_event_id: nsEventId,
          webhook_registry_id: nsWebhookId,
          is_enabled: true
        };

    try {
      const r = await createNotificationSetting(body);
      setNsResponse({ ok: true, text: JSON.stringify(r.data, null, 2) });
      showToast("Subscription added");
      // Clear the endpoint-only fields so the next subscription does not
      // silently inherit this one's mapping.
      setNsPayloadTemplate("");
      setNsDedupKeyTemplate("");
      setNsEndpointId("");
      loadNotificationSettingsList();
    } catch (e) {
      setNsResponse({
        ok: false,
        text: JSON.stringify(e.response?.data ?? e.message, null, 2)
      });
      showToast(`Failed: ${getErrorDetail(e)}`, true);
    }
  };

  const onToggleNS = async (id, enabled) => {
    try {
      await patchNotificationSetting(id, { is_enabled: enabled });
      showToast(enabled ? "Subscription enabled" : "Subscription disabled");
    } catch {
      showToast("Failed to update", true);
      loadNotificationSettingsList();
    }
  };

  const onDeleteNS = async (id) => {
    try {
      await deleteNotificationSetting(id);
      showToast("Subscription removed");
      loadNotificationSettingsList();
    } catch (e) {
      showToast(getErrorDetail(e), true);
    }
  };

  const onEnableLexAi = async () => {
    const id = lexaiApiId.trim();
    if (!id) {
      showToast("Provider API ID is required", true);
      return;
    }
    try {
      const r = await enableLexAi({ provider_api_id: id });
      setLexaiResponse({ ok: true, text: JSON.stringify(r.data, null, 2) });
      showToast(r.data?.message || "Enabled");
    } catch (e) {
      setLexaiResponse({
        ok: false,
        text: JSON.stringify(e.response?.data ?? e.message, null, 2)
      });
      showToast(`Failed: ${getErrorDetail(e)}`, true);
    }
  };

  const onLoadLogs = async () => {
    try {
      const params = { limit: logLimit };
      if (logFilter.trim()) params.event_type = logFilter.trim();
      const data = await fetchLexAiLogs(params);
      setLogs(data);
    } catch (e) {
      setLogs([]);
      showToast(getErrorDetail(e), true);
    }
  };

  const onTestNS = async (lexaiEventId) => {
    // "Test" is a misnomer: this fires the REAL dispatch path. For a webhook
    // that means a sample POST to the customer's URL, which is what people
    // expect. For a catalogued endpoint it means a real, credentialed write into
    // a third-party system of record — e.g. publishing an actual decline to a
    // carrier's event service, which they cannot retract. Confirm first, naming
    // the destination, rather than doing it on a single click.
    const affected = notificationRows.filter(
      (row) => row.lexai_event_id === lexaiEventId && row.is_enabled
    );
    const liveDestinations = affected.filter((row) => row.provider_api_endpoint_id);

    if (liveDestinations.length > 0) {
      const names = liveDestinations
        .map((row) => `• ${row.endpoint_method ?? ""} ${row.endpoint_path ?? row.endpoint_name ?? "endpoint"}`.trim())
        .join("\n");
      const proceed = window.confirm(
        `This sends a REAL request to ${liveDestinations.length} live endpoint(s), ` +
          `using your stored credentials:\n\n${names}\n\n` +
          `It is not a dry run — the receiving system will treat it as genuine ` +
          `data and may not be able to undo it.\n\nSend anyway?`
      );
      if (!proceed) return;
    }

    try {
      const r = await testLexAiWebhook({
        lexai_event_id: lexaiEventId,
        application_id: "app_test_001"
      });
      const n = r.data?.dispatched_to ?? 0;
      const suppressed = r.data?.suppressed ?? 0;
      const suppressedNote = suppressed
        ? `\n${suppressed} suppressed as duplicate(s) — already delivered, so nothing was re-sent.`
        : "";
      setNsTestResponse({
        ok: true,
        text: `✓ Fired — dispatched to ${n} destination(s)${suppressedNote}\n${JSON.stringify(r.data, null, 2)}`
      });
      showToast(
        n === 0 && suppressed === 0
          ? "Fired, but nothing matched — check the event has an enabled subscription"
          : `Fired — ${n} dispatched${suppressed ? `, ${suppressed} suppressed` : ""}`,
        n === 0 && suppressed === 0
      );
    } catch (e) {
      setNsTestResponse({
        ok: false,
        text: JSON.stringify(e.response?.data ?? e.message, null, 2)
      });
      showToast(`Test failed: ${getErrorDetail(e)}`, true);
    }
  };

  return {
    tab,
    setTab,
    toast,
    webhooks,
    whIntegrations,
    whName,
    setWhName,
    whUrl,
    setWhUrl,
    whAuthType,
    setWhAuthType,
    whOAuthConfig,
    setWhOAuthConfig,
    whFieldErrors,
    whResponse,
    loadWebhooks,
    loadWebhookIntegrations,
    onCreateWebhook,
    onPatchWebhook,
    onToggleWebhook,
    onDeleteWebhook,
    editingWebhook,
    setEditingWebhook,
    whPatchSaving,
    isWebhookAdmin: isAdminUser(),
    notificationRows,
    lexaiEvents,
    nsEventId,
    setNsEventId,
    nsWebhookId,
    setNsWebhookId,
    nsDestKind,
    setNsDestKind,
    nsEndpointId,
    setNsEndpointId,
    endpointCatalog,
    nsPayloadTemplate,
    setNsPayloadTemplate,
    nsDedupKeyTemplate,
    setNsDedupKeyTemplate,
    nsDedupWindow,
    setNsDedupWindow,
    nsResponse,
    nsTestResponse,
    activeWebhooks,
    loadNotificationSettingsList,
    loadLexAIEventsList,
    onCreateNS,
    onToggleNS,
    onDeleteNS,
    onTestNS,
    lexaiApiId,
    setLexaiApiId,
    lexaiResponse,
    onEnableLexAi,
    logFilter,
    setLogFilter,
    logLimit,
    setLogLimit,
    logs,
    onLoadLogs
  };
}
